import { FC, useState, useEffect, useRef } from 'react'
import { ReactComponent as FacesignIcon } from '../../shared/assets/facesignIcon.svg'

interface Props {
  statements: string[]
  intervalMs?: number
  onComplete?: () => void
  minStatements?: number
  shouldComplete?: boolean
}

const ReasoningLoader: FC<Props> = ({
  statements,
  intervalMs = 2000,
  onComplete,
  minStatements = 3,
  shouldComplete = false
}) => {
  const [currentStatement, setCurrentStatement] = useState<string>('')
  const [animationKey, setAnimationKey] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const shouldCompleteRef = useRef(shouldComplete)

  // Keep ref updated with latest shouldComplete value
  useEffect(() => {
    shouldCompleteRef.current = shouldComplete
    onComplete?.()
  }, [shouldComplete, onComplete])

  useEffect(() => {
    if (statements.length === 0) return

    // Show first statement immediately
    setCurrentStatement(statements[0])
    setAnimationKey(0)
    let currentIdx = 0

    const interval = setInterval(() => {
      currentIdx += 1

      // Check if we should complete early (data is ready and minimum met)
      if (shouldCompleteRef.current && currentIdx >= minStatements) {
        clearInterval(interval)
        onComplete?.()
        return
      }

      // Check if we've shown all statements
      if (currentIdx >= statements.length) {
        // If we've shown all statements and met minimum, complete
        if (currentIdx >= minStatements) {
          clearInterval(interval)
          onComplete?.()
        }
        return
      }

      // Replace with next statement and trigger animation
      setCurrentStatement(statements[currentIdx])
      setAnimationKey((prev) => prev + 1)
    }, intervalMs)

    return () => clearInterval(interval)
  }, [statements, intervalMs, onComplete, minStatements])

  if (statements.length === 0) return null

  return (
    <div ref={containerRef} className='flex items-center gap-3 pt-4 pb-8'>
      {/* Animated FaceSign Icon */}
      <div
        className='flex-shrink-0 w-8 h-8 animate-pulse-scale'
        style={{ color: '#16C95D' }}
      >
        <FacesignIcon className='w-full h-full' />
      </div>

      {/* Single Reasoning Text with pop animation */}
      <div className='flex-1 min-w-0'>
        <p key={animationKey} className='text-base text-gray-600 animate-pop'>
          {currentStatement}
        </p>
      </div>
    </div>
  )
}

export default ReasoningLoader
